import type { FC, RefAttributes } from 'react';
import { useCallback, useEffect, useRef, useState, Fragment } from 'react';

import { L10nText } from '../../../../../services/uiTranslations/reactBindings/L10nText';
import type { Message } from '../../../../../store/slices/chat/types';
import { useModifiers } from '../../../../../styles/hooks/useModifiers';
import { IconButton, IconButtonSize, Icons } from '../../../../controls/iconButton/IconButton';
import { MessageBubble } from '../messageBubble/MessageBubble';

import styles from './MessagesWrapper.module.css';

export interface MessagesWrapperProps extends RefAttributes<HTMLDivElement> {
  messages: Message[];
  currentUserId: string;
  currentUserName?: string;
  chatOptionsVisible?: boolean;
}

export const MessagesWrapper: FC<MessagesWrapperProps> = ({
  messages,
  currentUserId,
  currentUserName,
  chatOptionsVisible,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [unreadMessageId, setUnreadMessageId] = useState<string | null>(null);
  const [showScrollIcon, setShowScrollIcon] = useState<boolean>(false);

  const lastMessage = messages[0];
  // Store last message in ref for performance optimisation
  const lastMessageRef = useRef(lastMessage);
  const prevReadMessageId = useRef<string | null>(lastMessage?.messageId ?? null);

  const messageWrapperMods = useModifiers(styles, 'messagesWrapper', { chatOptionsVisible }, { withBaseClass: true });

  const isNearBottom = useCallback((element: HTMLDivElement): boolean => Math.abs(element.scrollTop) <= 45, []); // Magic threshold of message and unread message indicator heights combined

  useEffect(() => {
    lastMessageRef.current = lastMessage;
    const wrapper = wrapperRef.current;
    if (wrapper && lastMessage) {
      const isCloseToBottom = isNearBottom(wrapper);
      if (!isCloseToBottom) {
        setUnreadMessageId(lastMessage.messageId);
      }
    }
  }, [isNearBottom, lastMessage]);

  const scrollToBottom = useCallback((): void => {
    if (wrapperRef.current) {
      wrapperRef.current.scrollTop = 0;
      prevReadMessageId.current = lastMessageRef.current?.messageId ?? null;
      setUnreadMessageId(null);
    }
  }, []);

  // Automatically scroll to bottom when current user posts a message
  useEffect(() => {
    if (lastMessage?.userId === currentUserId) {
      scrollToBottom();
    }
  }, [currentUserId, lastMessage, scrollToBottom]);

  // Mark last message as unread when user scrolls up
  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (wrapper) {
      const handleScroll = (): void => {
        const lastMessageId = lastMessageRef.current?.messageId ?? null;
        const isCloseToBottom = isNearBottom(wrapper);

        if (isCloseToBottom) {
          prevReadMessageId.current = lastMessageId;
          setShowScrollIcon(false);
          setUnreadMessageId(null);
        } else {
          setShowScrollIcon(true);
          if (!unreadMessageId && lastMessageId !== prevReadMessageId.current) {
            setUnreadMessageId(lastMessageId);
          }
        }
      };

      wrapper.addEventListener('scroll', handleScroll);

      return (): void => {
        wrapper.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isNearBottom, unreadMessageId]);

  return (
    <div className={styles.container}>
      <div className={messageWrapperMods} ref={wrapperRef}>
        {messages.map((message: Message) =>
          message.showMessage ? (
            <Fragment key={message.messageId}>
              {message.messageId === unreadMessageId ? (
                <div className={styles.messagesWrapper__unreadMessage}>
                  <L10nText tk="UNREAD_MESSAGES" />
                </div>
              ) : null}
              <div className={styles.messagesWrapper__message}>
                <MessageBubble {...message} currentUserId={currentUserId} currentUserName={currentUserName} />
              </div>
            </Fragment>
          ) : null
        )}
      </div>

      {showScrollIcon ? (
        <div className={styles.messagesWrapper__scrollButton}>
          <IconButton
            icon={Icons.ChevronDown}
            size={IconButtonSize.LARGE}
            onClick={scrollToBottom}
            badge={unreadMessageId !== null}
          />
        </div>
      ) : null}
    </div>
  );
};

MessagesWrapper.displayName = 'MessagesWrapper';
